(ns frontend.view.modals.curtains.pass-curtain.view
  (:require
   [clojure.string :as string]
   [common.currencies :as currencies]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.entities.pipeline :as pipeline]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [frontend.state.permissions :as f-permissions]
   [frontend.updates.entity-scope :as entity-scope]
   [frontend.utils :as fu]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.curtains.opportunity-load :as opportunity-load]
   [frontend.view.modals.curtains.pass-curtain.events :as events]
   [potpuri.core :refer [fn->]]
   [reagent.core :as r]
   [ui.color :as c]
   [ui.components.input.checkbox :as checkbox]
   [ui.components.modal.view :as modal]
   [ui.components.toggle.view :as toggle]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.state.named-values :as named-values]))

(defonce modal-key :curtain/pass)

(defn- apply-reason-template!
  "Fills the notification message from the email template of the pass reason.
   It does this only if that reason is still the selected reason.

   The new grid does not bulk-sync the template, so the function fetches it first.
   The entity-scope of the pass reason includes the templates that point at it."
  [{:keys [edit-state reason-id]}]
  (let [apply! (fn []
                 (when (= reason-id (get @edit-state :oppo/pass-reason-v2))
                   (swap! edit-state assoc
                          :message
                          (:email-template/body (session/email-template-for reason-id)))))]
    (if (session/new-grid-2023?)
      (entity-scope/fetch-once! {:entity-id reason-id
                                 :on-done   (fn [_entities] (apply!))
                                 :on-error  (fn [_error] nil)})
      (apply!))))

(defn pass-reason-selector
  [{:keys [_edit-state _attribute _pipeline-id]}]
  ;; The option that the user picked or created last. On the new grid, the
  ;; session cannot give the label of a named-value. The select must always get
  ;; a value that has a label.
  (let [picked* (r/atom nil)]
    (fn [{:keys [edit-state attribute pipeline-id]}]
      (let [value (get @edit-state attribute)
            picked @picked*
            label (or (when (= (:value picked) value)
                        (:label picked))
                      (some-> (or (session/get-entity value)
                                  (named-values/row-by-id attribute value))
                              (entity/display-string)))
            value-opt (when (and value label)
                        {:value value
                         :label label})]
        [:div
         [labelled-input/view
          {:editor-type           :editor.type/search
           :named-value-attribute attribute
           ;; Without the pipeline, a reason that is archived in it is offered.
           :creation-pipeline-id  pipeline-id
           :mandatory?            true
           :field/entity-types    [:entity.type/named-value]
           :create!               (fn [input-str]
                                    (f-entity/open-creator!
                                     [:entity.type/named-value]
                                     {:start-value           input-str
                                      :named-value-attribute attribute
                                      :skip-success-msg?     true
                                      :on-ok!                (fn [{:keys [new-entity-id new-entity close-cb!]}]
                                                               (named-values/remember-row! new-entity)
                                                               (swap! edit-state assoc attribute new-entity-id)
                                                               (reset! picked* {:value new-entity-id
                                                                                :label (:named-value/value new-entity)})
                                                               (when (fn? close-cb!)
                                                                 (close-cb!)))}))
           :value                 value-opt
           :label                 (loc/tr attribute)
           :on-change             (fn [selected-value selected-option]
                                    (reset! picked* selected-option)
                                    (if (nil? selected-value)
                                      (swap! edit-state dissoc attribute :message)
                                      (do
                                        (swap! edit-state assoc attribute selected-value)
                                        (when (= attribute :oppo/pass-reason-v2)
                                          (apply-reason-template! {:edit-state edit-state
                                                                   :reason-id  selected-value})))))}]]))))

(defn internal-comment-input
  [edit-state]
  (let [attribute :internal-comment
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:label     (loc/tr :pass-curtain/internal-pass-comment)
       :data-type :data.type/text-simple
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn broken-deal-expenses
  [edit-state]
  (let [attribute :broken-deal-expenses
        currency-options (->> currencies/options
                              (map (fn [opt]
                                     {:value (:key opt)
                                      :label (:label opt)})))]
    [:div
     {:class "row"}
     [:div
      {:class "col-4"}
      [labelled-input/view
       {:data-type :data.type/currency-symbol
        :label     (loc/tr :oppo/cur-broken-deal-expenses--sym)
        :value     (->> currency-options
                        (cu/filter-by :value (or (get @edit-state :broken-deal-currency)
                                                 (:tenant/default-currency @session/tenant)))
                        (first))
        :options   currency-options
        :on-change (fn [new-value]
                     (if (nil? new-value)
                       (swap! edit-state dissoc :broken-deal-currency new-value)
                       (swap! edit-state assoc :broken-deal-currency new-value)))}]]
     [:div
      {:class "col-8"}
      [labelled-input/view
       {:data-type :data.type/currency-value
        :label     (loc/tr :oppo/cur-broken-deal-expenses)
        :value     (get @edit-state attribute)
        :on-change (fn [new-value]
                     (if (nil? new-value)
                       (swap! edit-state dissoc attribute new-value)
                       (swap! edit-state assoc attribute new-value)))}]]]))

(defn notification-toggle
  [edit-state read-only?]
  (let [attribute :send-notification
        value (get @edit-state attribute)]
    [:div
     {:class "flex items-center gap-2"}
     [:label (loc/tr :pass-curtain/send-rejection-notification)]
     [toggle/toggle
      {:checked?  (if read-only?
                    false
                    value)
       :disabled? read-only?
       :on-change (fn [checked?]
                    (swap! edit-state assoc attribute checked?))}]]))

(defn to-selector
  [edit-state persons]
  (let [attribute :notify-to
        read-only? (= (count persons) 1)
        value (get @edit-state attribute)
        options (->> persons
                     (map (fn [c]
                            {:value (:db/id c)
                             :label (entity/display-string c :long)})))]
    [:div
     [labelled-input/view
      {:editor-type :editor.type/select
       :label       (str (loc/tr :email/to) ":")
       :disabled?   read-only?
       :options     options
       :value       (->> options
                         (cu/filter-by :value value)
                         (first))
       :on-change   (fn [selected-value _selected-option]
                      (if (nil? selected-value)
                        (swap! edit-state dissoc attribute selected-value)
                        (swap! edit-state assoc attribute selected-value)))}]]))

(defn subject-input
  [edit-state]
  (let [attribute :subject
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-simple
       :label     (loc/tr :subject)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn message-input
  [edit-state]
  (let [attribute :message
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-multiline
       :label     (loc/tr :message)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn footer-input
  [edit-state]
  (let [attribute :footer
        value (get @edit-state attribute)]
    [:div
     [labelled-input/view
      {:data-type :data.type/text-multiline
       :label     (loc/tr :email-footer)
       :value     value
       :on-change (fn [new-value]
                    (if (nil? new-value)
                      (swap! edit-state dissoc attribute new-value)
                      (swap! edit-state assoc attribute new-value)))}]]))

(defn save-default-checkbox
  [edit-state]
  (let [attribute :save-default
        value (get @edit-state attribute)]
    [:div
     [:div
      {:class "flex gap-3"}
      [checkbox/view
       {:value     value
        :on-change (fn [checked?]
                     (swap! edit-state assoc attribute checked?))}]
      [:span
       {:class "mb-0 text-sm"}
       (loc/tr :pass-curtain/make-this-default)]]]))

(defn loaded-view
  "The curtain, for a deal that is loaded. The deal is `:opportunity` in the
   arguments."
  [{:keys [state props opportunity _shown? _close-modal! _topmost?]}]
  (let [edit-state (r/cursor state [:edit])
        oppo-id (:oppo-id props)
        first-contact-person (->> oppo-id
                                  (fu/resolve-contact-persons)
                                  (remove (fn-> :person/universal-email-address string/blank?))
                                  first)
        subject (loc/tr :pass-curtain/default-subject [(:oppo/name opportunity)])
        footer (loc/tr :pass-curtain/default-footer [(:account/first-name @session/account)])
        _ (swap! edit-state assoc
                 :subject subject
                 :footer footer)
        _ (when first-contact-person
            (swap! edit-state assoc :notify-to (:db/id first-contact-person)))
        ;; The component can choose the default reason only when it knows the
        ;; options. On the new grid they arrive from a fetch after the mount.
        default-reason-chosen?* (atom false)
        state-atom (r/cursor state [:state-atom])
        nylas-enabled? (session/nylas-active?)]
    (fn [{:keys [shown? close-modal! props state topmost? opportunity]}]
      (let [{:keys [header-text
                    oppo-id reset-focus!]}
            props
            session-entities @session/entities
            deal opportunity
            pipeline-id (pipeline/containing-pipeline-id deal)
            deal-name (:oppo/name deal)
            header-text (or header-text (loc/tr :pass-curtain/title [deal-name]))
            first-pass-reason (->> (named-values/usable-rows :oppo/pass-reason-v2 pipeline-id)
                                   (sort-by :common/ord)
                                   (first))
            _ (when (and first-pass-reason
                         (not @default-reason-chosen?*))
                (reset! default-reason-chosen?* true)
                (when (nil? (get @edit-state :oppo/pass-reason-v2))
                  (swap! edit-state assoc :oppo/pass-reason-v2 (:db/id first-pass-reason))
                  (apply-reason-template! {:edit-state edit-state
                                           :reason-id  (:db/id first-pass-reason)})))
            close-modal! (fn []
                           (close-modal!)
                           (when reset-focus!
                             (reset-focus!)))
            edit-vals @edit-state
            {:keys [subject message notify-to]} edit-vals

            ;; The picker offers only live options, so a chosen id is usable. Do not
            ;; look up the reason in the session. The new grid does not sync it,
            ;; and a missing entity would keep the Confirm button disabled.
            pass-reason-id (:oppo/pass-reason-v2 edit-vals)
            pass-reason-disabled? (or (nil? pass-reason-id)
                                      (:common/disabled? (get session-entities pass-reason-id)))

            contact-persons (->> oppo-id
                                 (fu/resolve-contact-persons)
                                 (remove :common/disabled?)
                                 (remove (fn-> :person/universal-email-address string/blank?)))
            no-contacts? (empty? contact-persons)
            notify? (:send-notification edit-vals)
            _ (when (and no-contacts?
                         notify?)
                (swap! edit-state assoc :send-notification false))

            one-contact? (= (count contact-persons) 1)
            first-contact-id (when one-contact?
                               (-> contact-persons
                                   first
                                   :db/id))
            _ (when (and one-contact?
                         (not= notify-to first-contact-id))
                (swap! edit-state assoc :notify-to first-contact-id))

            contact (->> notify-to
                         (get session-entities))
            not-contact-person? (->> contact-persons
                                     (filter (fn-> :db/id (= notify-to)))
                                     empty?)
            no-contact? (or not-contact-person?
                            (nil? contact)
                            (:common/disabled? contact))
            no-email? (->> contact
                           :person/universal-email-address
                           string/blank?)

            empty-subject? (string/blank? subject)
            empty-message? (string/blank? message)

            button-disabled? (if (and notify? (not nylas-enabled?))
                               (or pass-reason-disabled?
                                   no-email?
                                   no-contact?
                                   empty-subject?
                                   empty-message?)
                               pass-reason-disabled?)
            _error-text (if (and notify? (not nylas-enabled?))
                          (cond
                            pass-reason-disabled?
                            (loc/tr :pass-curtain/select-pass-reason)
                            no-contact?
                            (loc/tr :pass-curtain/error-no-contact)
                            no-email?
                            (loc/tr :pass-curtain/error-no-email)
                            empty-subject?
                            (loc/tr :pass-curtain/error-empty-subject)
                            empty-message?
                            (loc/tr :pass-curtain/error-empty-message)
                            :else nil)
                          (cond pass-reason-disabled?
                                (loc/tr :pass-curtain/select-pass-reason)
                                :else nil))

            reject! (fn []
                      (events/on-pass state deal close-modal!))]
        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :color         ::c/danger
          :close-button? true}
         header-text
         [:div
          {:class "flex flex-col gap-4 flex-auto"}
          [pass-reason-selector
           {:edit-state  edit-state
            :attribute   :oppo/pass-reason-v2
            :pipeline-id pipeline-id}]

          (when (f-permissions/field-write-access? :oppo/pass-reason-secondary
                                                   :entity.type/oppo
                                                   pipeline-id)

            [pass-reason-selector
             {:edit-state  edit-state
              :attribute   :oppo/pass-reason-secondary
              :pipeline-id pipeline-id}])

          (when (f-permissions/field-write-access? :oppo/cur-broken-deal-expenses
                                                   :entity.type/oppo
                                                   pipeline-id)
            [broken-deal-expenses edit-state])

          [internal-comment-input edit-state state-atom deal]

          [:div
           {:class "flex flex-col gap-2"}
           [:div
            {:class "flex gap-2 items-base text-base-light-text-paler text-xs"}
            [:i.fal.fa-user-secret]
            [:div
             (loc/tr :pass-curtain/private-info)]]

           [notification-toggle edit-state no-contacts?]]

          (when (and notify?
                     (not nylas-enabled?))
            [:div
             {:class "flex flex-col gap-4"}
             [to-selector edit-state contact-persons]
             [subject-input edit-state]
             [message-input edit-state]
             [footer-input edit-state]
             (when (session/is-admin?)
               [save-default-checkbox edit-state])])

          (when (and notify?
                     nylas-enabled?)
            [:div.infobox.infobox--info
             (loc/tr :pass-reject-nylas-info)])]
         [:<>
          [modal/left-cancel-only close-modal! nil]
          [modal/right-single-button
           ::c/primary
           button-disabled?
           #(reject!)
           (if notify?
             (loc/tr :pass-curtain/reject-and-notify)
             (loc/tr :pass-curtain/reject-without-notifying))
           nil]]]))))

(defn view
  [{:keys [props] :as args}]
  [opportunity-load/with-opportunity
   {:opportunity-id (:oppo-id props)
    :args           args
    :loaded-view    loaded-view
    :header-text    (or (:header-text props)
                        (loc/tr :pass-curtain/title [nil]))}])
